import React from 'react';
import { cn } from '../../lib/utils';

/**
 * Verified Badge Component
 * Shows a blue checkmark for verified creators (50K+ followers)
 * 
 * Badge Types:
 * - verified: Blue checkmark (auto at 50K followers)
 * - creator: Star badge (monetization enabled)
 * - official: Gold checkmark (FityH official accounts)
 */
export const VerifiedBadge = ({ 
  isVerified, 
  verificationType = 'verified',
  size = 'md',
  showTooltip = true,
  className 
}) => {
  if (!isVerified) return null;
  
  const sizeClasses = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };
  
  const badgeConfig = {
    verified: {
      color: 'text-blue-500',
      bgColor: 'bg-blue-500',
      title: 'Verified Creator (50K+ followers)',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
          <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z"/>
        </svg>
      )
    },
    creator: {
      color: 'text-purple-500',
      bgColor: 'bg-purple-500',
      title: 'Creator (Monetization Enabled)',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      )
    },
    official: {
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-500',
      title: 'Official FityH Account',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
          <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z"/>
        </svg>
      )
    },
    early_adopter: {
      color: 'text-green-500',
      bgColor: 'bg-green-500',
      title: 'Early Adopter',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
      )
    }
  };
  
  const config = badgeConfig[verificationType] || badgeConfig.verified;
  
  return (
    <span 
      className={cn(
        'inline-flex items-center justify-center shrink-0',
        sizeClasses[size],
        config.color,
        className
      )}
      title={showTooltip ? config.title : undefined}
      data-testid="verified-badge"
    >
      {config.icon}
    </span>
  );
};

/**
 * Inline verified name with badge
 */
export const VerifiedName = ({ 
  name, 
  isVerified, 
  verificationType = 'verified',
  size = 'md',
  className 
}) => {
  return (
    <span className={cn('inline-flex items-center gap-1', className)}>
      <span className="truncate">{name}</span>
      <VerifiedBadge 
        isVerified={isVerified} 
        verificationType={verificationType}
        size={size}
      />
    </span>
  );
};

export default VerifiedBadge;
